import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import FoodProfileCard from '../components/FoodProfileCard';
import { foodProfiles, categories } from '../data/foodProfiles';

const FoodNutritionPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter foods
  const filteredFoods = foodProfiles.filter(food => {
    const matchesCategory = selectedCategory === 'All' || food.category === selectedCategory;
    const matchesSearch = food.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/integrative-health"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Integrative Health
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Food & Nutrition
        </h1>
        
        {/* Contextual Introduction */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Why people explore food
          </h2>
          
          <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              Food is an area many people with CLL choose to explore. For some, it's about feeling more in control during watch and wait. For others, it's rooted in cultural traditions, family practices, or a personal interest in whole foods and nutrition.
            </p>
            
            <p>
              The food profiles here are examples, not instructions. They represent foods that appear frequently in discussions about anti-inflammatory eating, Mediterranean diets, or plant-forward approaches. These patterns are often explored alongside medical care, not as replacements for it.
            </p>
            
            <p>
              Cultural and traditional relevance matters. Many of these foods have been used in various cuisines for generations — turmeric in South Asian cooking, fermented foods in Korean traditions, olive oil in Mediterranean cultures. This isn't about adopting a specific diet; it's about understanding why certain foods are discussed.
            </p>
            
            <p className="font-medium text-slate-900 pt-2 border-t border-slate-200">
              Individual tolerance varies significantly. What works for one person may not work for another. Some people find certain foods helpful; others notice no difference. If you're interested in dietary changes, discuss them with your healthcare team or a registered dietitian who understands CLL.
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          This information is provided for educational purposes only and does not replace professional medical advice. Always consult your healthcare provider before making dietary, supplement, or lifestyle changes, especially if you are immunocompromised, have low blood counts, or are undergoing treatment.
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search foods..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedCategory('All')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === 'All'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All ({foodProfiles.length})
        </button>
        {categories.map(category => {
          const count = foodProfiles.filter(f => f.category === category).length;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="text-sm text-slate-600">
        Showing {filteredFoods.length} of {foodProfiles.length} foods
        {searchTerm && ` for "${searchTerm}"`}
      </div>

      {/* Food Cards */}
      <div className="space-y-4">
        {filteredFoods.length > 0 ? (
          filteredFoods.map((food) => (
            <FoodProfileCard key={food.id} food={food} />
          ))
        ) : (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <p className="text-slate-600">
              No foods found matching your criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodNutritionPage;
